<template>
  <div style="width: 308px">
    <div class="switch-container" style="margin-bottom: 20px">
      <label for="switch">Embedding in Web Page</label>
      <InputSwitch class="smaller-switch" v-model="infiniteScroll" id="switch" />
      <IconLayout class="action"   :style="{ marginLeft: '60px', backgroundColor: advancedLayout ? '#EEE' : '#FFF' }" @click="toggleAdvancedLayout"></IconLayout>
    </div>

    <ContainerCoordinates></ContainerCoordinates>

    <div style="margin-top: 5px" v-if="selectedSectionRow">
      <div style="margin-top: 10px; margin-bottom: 30px" v-if="advancedLayout">
        <div style="display: inline-block; width: 80px">Sections</div>
        <Button
            icon="pi pi-plus"
            rounded
            outlined
            aria-label="Add New Section"
            @click="createSlideLayout()"
            style="margin-left: 180px"
        />
        <div style="margin-top: 10px; width: 310px; margin-left: 0px; border: solid 1px #ccc; padding: 10px;">

          <Dropdown v-model="selectedSectionLayout" :options="wrappedSectionLayouts" style="width: 230px;">
            <template #value="slotProps">
              <div
                  class="row align-items-center"
                  style="width: 100%; padding-left: 20px; padding-right: 20px"
                  v-if="slotProps.value"
              >
                <div
                    v-for="child in slotProps.value.layout.children"
                    :key="child.id"
                    class="col section-block"
                    :style="{ flexGrow: child['flex-grow'] }"
                />
              </div>
            </template>
            <template #option="slotProps">
              <div class="row align-items-center" style="width: 100%; padding-left: 20px; padding-right: 20px">
                <div
                    v-for="child in slotProps.option.layout.children"
                    :key="child.id"
                    class="col section-block"
                    :style="{ flexGrow: child['flex-grow'] }"
                />
              </div>
            </template>
          </Dropdown>
          <Button icon="pi pi-trash" style="margin-left: 5px;" @click="deleteSectionRow()" />
        </div>
      </div>

      <SectionLayout :onDeleteRow="deleteSectionRow"></SectionLayout>

      <div style="width: 320px;" v-if="activeId || sectionSelected || subsectionSelected">
        <TabView v-model:activeIndex="activeTabIndex" style="margin-top: 10px;">
          <TabPanel v-if="selectedSectionId" :header="sectionName">
            <StyleEditor element-type="section" :active-tab="activeTabIndex === 0 ? 'Section' : null"/>
          </TabPanel>
          <TabPanel v-if="selectedSubsectionId" header="Subsection">
            <StyleEditor element-type="subsection" :active-tab="activeTabIndex === 1 ? 'Subsection' : null" />
          </TabPanel>
          <TabPanel header="Tile" v-if="selectedWidget && Object.keys(selectedWidget).length > 0 && activeId">
            <StyleEditor element-type="element" :active-tab="activeTabIndex === 2 ? 'Tile' : null" />
          </TabPanel>
        </TabView>

      </div>

      <Dialog v-model:visible="openWizardDialog" modal :header="stepperTitle">
        <WizardForm :slide="stepperSlide" @close-dialog="openWizardDialog = false" />
      </Dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import {storeToRefs} from 'pinia'
import {useMainStore, useSlidesStore, useWidgetStore} from "@/store";
import { PPTDragDropElement, PPTSlideLayoutElement} from "@/types/slides";
import useMicroservice from "@/hooks/useMicroservice";
import {computed, onMounted, ref, watch} from "vue";
import Dropdown from "primevue/dropdown";
import {slideLayouts} from "@/mocks/slideLayouts"
import Button from "primevue/button";
import {nanoid} from "nanoid";
import useCreateElement from "@/hooks/useCreateElement";
import useHistorySnapshot from "@/hooks/useHistorySnapshot";
import WizardForm from "@/views/Editor/CanvasTool/components/WizardForm.vue"
import ContainerCoordinates from "./SectionPanel/ContainerCoordinates.vue"
import SectionLayout from "./SectionPanel/SectionLayout.vue"
import StyleEditor from "@/views/Editor/Toolbar/StyleEditor.vue"

const slidesStore = useSlidesStore()
const widgetStore = useWidgetStore()
const {
  activeId,
  selectedDragDropId,
  selectedSectionId,
  selectedSectionRowId,
  selectedSubsectionId,
  selectedSubsectionRowId,
  selectedWidget,
  selectedWidgets,
  sectionSelected,
  subsectionSelected
} = storeToRefs(widgetStore)

const {
  updated,
  advancedLayout
} = storeToRefs(useMainStore())


const openWizardDialog = ref(false)

const stepperTitle = ref('')
const stepperSlide = ref()

const infiniteScroll = computed({
  get: () => {
    return slidesStore.currentSlide.infiniteScroll ?? false
  },
  set: val => {
    slidesStore.updateSlide({ infiniteScroll: val })
  }
})

const matches = (layout1, layout2) => {
  if (!layout1.children || !layout2.children || layout1.children.length != layout2.children.length) {
    return false
  }

  for (let i = 0; i < layout1.children.length; i++) {
    if (layout1.children[i]['flex-grow'] != layout2.children[i]['flex-grow']) {
      return false
    }
  }

  return true
}

const selectedSectionLayout = computed({
  get() {
    const sectionRow = getSelectedSectionRow()
    for (const layout of slideLayouts) {
      if (matches(layout, sectionRow)) {
        return {
          layout: layout
        }
      }
    }
    return {
      layout: {}
    }
  },
  set(val) {
    const sectionRow = getSelectedSectionRow()
    const newLayout = val.layout

    while (newLayout.children.length > sectionRow.children.length) {
      const newId = nanoid(10)
      sectionRow.children.push({
        id: newId,
        style: {
          valign: 'center',
          justifyContent: 'center',
          align: 'center'
        }
      })

      createElement({
        type: 'dragdrop',
        id: newId,
        left: 0,
        top: 0,
        width: 1366,
        height: 768,
        content: '',
        rotate: 0,
        defaultFontName: 'Arial',
        defaultColor: '',
        layout: [{
          'type': 'row',
          'id': nanoid(10),
          'children': [
            {
              'id': nanoid(10),
              'style': {
                valign: 'center',
                align: 'center',
                justifyContent: 'center',
                margin: 5
              }
            }
          ],
          'style': {
            'backgroundColor': 'transparent',
            'clipTop': 10,
            'clipRight': 10,
            'clipBottom': 10,
            'clipLeft': 10,
            'borderRadius': 10
          }
        }],
        widgets: []
      })
    }

    while (newLayout.children.length < sectionRow.children.length) {
      sectionRow.children.pop()
      // TODO delete the dropzone for this section
    }

    for (let i = 0; i < newLayout.children.length; i++) {
      if (newLayout.children[i]['flex-grow']) {
        sectionRow.children[i]['flex-grow'] = newLayout.children[i]['flex-grow']
      } else {
        delete sectionRow.children[i]['flex-grow']
      }
    }

    const slideLayoutElement: PPTSlideLayoutElement = <PPTSlideLayoutElement>getSlideLayoutElement()
    const layout = slideLayoutElement.layout
    slidesStore.updateElement({id: slideLayoutElement.id, props: {layout: layout}})
    api.savePresentation()
    addHistorySnapshot()
  }
})

onMounted(() => {

})


const sectionName = computed(() => advancedLayout.value ? 'Section' : 'Container')
const selectedSectionRow = computed(() => getSelectedSectionRow())

const wrappedSectionLayouts = computed(() => {
  const results = [] as any

  for (let index = 0; index < slideLayouts.length; index++) {
    results.push({
      index: index,
      layout: slideLayouts[index]
    })
  }

  return results
})


const api = useMicroservice()

const toggleAdvancedLayout = () => {
  advancedLayout.value = !advancedLayout.value
}

const getSlideLayoutElement = () => {
  return slidesStore
      .getElementsByType('slidelayout')
      .find((element) =>
          element.layout.children.some((row) =>
              row.children.some((section) => section.id === selectedSectionId.value)
          )
      )
}

const getSelectedSectionRow = () => {

  const slideLayoutElement: PPTSlideLayoutElement = <PPTSlideLayoutElement>getSlideLayoutElement()

  if (slideLayoutElement) {
    const layout = slideLayoutElement.layout
    for (const child of layout.children) {
      if (child.id === selectedSectionRowId.value) {
        return child
      }
    }
  }
}

const {addHistorySnapshot} = useHistorySnapshot()
const {
  createElement,
  createSlideLayoutElement,
} = useCreateElement()


const createSlideLayout = () => {
  const rowJson = {
    'type': 'row',
    'children': [
      {
        'id': 1
      }
    ]
  }
  createSlideLayoutElement()

  const tempSubsectionId = nanoid(10)
  const tempSubsectionRowId = nanoid(10)
  const tempSectionRowId = nanoid(10)
  const tempSectionId = nanoid(10)

  const _row = JSON.parse(JSON.stringify(rowJson))
  _row.id = tempSectionRowId

  const child = _row.children[0]
  child.id = tempSectionId
  child.style = {
    valign: 'center',
    align: 'center',
    justifyContent: 'center'
  }

  createElement({
    type: 'dragdrop',
    id: child.id,
    left: 0,
    top: 0,
    width: 1366,
    height: 768,
    content: '',
    rotate: 0,
    defaultFontName: 'Arial',
    defaultColor: '',
    layout: [{
      'type': 'row',
      'id': tempSubsectionRowId,
      'children': [
        {
          'id': tempSubsectionId,
          'style': {
            valign: 'center',
            align: 'center',
            justifyContent: 'center',
            margin: 5
          }
        }
      ],
      'style': {
        'backgroundColor': 'transparent',
        'clipTop': 10,
        'clipRight': 10,
        'clipBottom': 10,
        'clipLeft': 10,
        'borderRadius': 10,
        valign: 'center',
        align: 'center',
        justifyContent: 'center'
      }
    }],
    widgets: []
  })


  const slideLayoutElement: PPTSlideLayoutElement = <PPTSlideLayoutElement>getSlideLayoutElement()
  const layout = slideLayoutElement.layout

  const index = layout.children.findIndex((row) => row.id === selectedSectionRowId.value)
  layout.children.splice(index + 1, 0, _row)

  slidesStore.updateElement({id: slideLayoutElement.id, props: {layout: layout}})

  selectedSectionRowId.value = tempSectionRowId
  selectedSectionId.value = tempSectionId
  selectedSubsectionId.value = tempSubsectionId
  selectedSubsectionRowId.value = tempSubsectionRowId
  selectedDragDropId.value = tempSectionId

  subsectionSelected.value = true
  activeId.value = ''
  selectedWidgets.value =  []


  api.savePresentation()
  addHistorySnapshot()

}

const getSelectedDragDrop = () => {
  if (selectedDragDropId.value) {
    for (const dragdrop of slidesStore.getElementsByType('dragdrop')) {
      if (dragdrop.id === selectedDragDropId.value) {
        return <PPTDragDropElement>dragdrop
      }
    }
  }

  return <PPTDragDropElement>slidesStore.getElementsByType('dragdrop')[0]
}

const { activeElementIdList } = storeToRefs(useMainStore())
const activeContainer = computed(() => {
  for (const section of slidesStore.getElementsByType('section')) {
    if (activeElementIdList.value.includes(section.id)) {
      return section
    }
  }
})


const deleteSectionRow = () => {
  const slideLayoutElement: PPTSlideLayoutElement = <PPTSlideLayoutElement>getSlideLayoutElement()
  const children = slideLayoutElement.layout.children.filter((row) => row.id !== selectedSectionRowId.value)

  deleteDragDrop()

  if (children.length == 0 && activeContainer.value) {
      slidesStore.deleteElement(slideLayoutElement.id)
      slidesStore.deleteElement(activeContainer.value.id)
  }
  else {
    slidesStore.updateElement({id: slideLayoutElement.id, props: {layout: {type: 'layout', children: children}}})
  }

  selectedSectionId.value = ''
  selectedSubsectionRowId.value = ''
  selectedSubsectionId.value = ''
  updated.value = Date.now()
  api.savePresentation()
  addHistorySnapshot()
}

const deleteDragDrop = () => {
  const dragDropElement = getSelectedDragDrop()
  slidesStore.deleteElement(dragDropElement.id)
}


const activeTabIndex = ref(0);

// Other existing code...

// Watch for changes and update the active tab
watch([activeId, selectedWidget, sectionSelected, subsectionSelected], () => {
  if (!activeId.value) {
    if (!sectionSelected.value && !subsectionSelected.value) {
      activeTabIndex.value = -1
    } else {
      // At least one of section or subsection is selected but no widget
      activeTabIndex.value = sectionSelected.value ? 0 : 1
    }
  } else {
    if (sectionSelected.value) {
      activeTabIndex.value = 0
    } else if (subsectionSelected.value) {
      activeTabIndex.value = 1
    } else if (selectedWidget && Object.keys(selectedWidget).length > 0) {
      activeTabIndex.value = 2
    }
  }
}, { immediate: true });
</script>

<style lang="scss" scoped>
:deep(.p-skeleton::after) {
  animation: none
}

:deep(.p-skeleton) {
  background-color: #DFDFDF;
}

:deep(.flex) {
  display: flex;
  cursor: pointer;
}

:deep(ul) {
  list-style-type: none;
}

:deep(.group) {
  margin-top: 10px;
  display: flex;
  align-items: center;
  column-gap: 10px;
}

.section-block {
  background: purple;
  font-size: 11px;
  text-align: center;
  text-indent: -9999em;
  padding: 2px 0;
  -webkit-transition: background .2s ease;
  transition: background .2s ease;
  border-radius: 3px;
  height: 20px;
  margin: 0 2px 3px;
}

.selected {
  border: solid 2px #2196F3 !important;
}

.smaller-switch {
  transform: scale(0.6);
}

.switch-container {
  display: flex;
  align-items: center;
  gap: 10px; /* Optional: Adds space between the label and switch */
}

:deep(.p-tabview .p-tabview-panels) {
  padding: 0.5rem
}

:deep(.p-tabview-nav-container) {
  position: sticky;
  top: 0;
  z-index: 99;
}

.action {
  font-size: 17px;
  border: 1px solid #cccccc;
  border-radius: 5px;
  padding: 5px;
}

.icon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  gap: 1px;
  margin-top: 10px;
  margin-bottom: 5px;
}

.inline-block {
  display: inline-block;
}

:deep(.p-button) {
  background: #fff;
  border: 1px solid #ccc;
  color: black;
}

</style>
